import { useMemo } from 'react';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { useField } from 'formik';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import InputWrapper from '../InputWrapper';
import { important } from '../../../../utils/global';
import { parsePlaceResult } from '../../../../utils/maps';
import { PLACES_API_KEY } from '../../../../maps';

interface AddressInputProps {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  onPlaceSelected: (address: {
    streetAddress: string;
    streetAddress2: string | null;
    city: string;
    state: string;
    zip: string;
  }) => void;
}

function AddressInput({
  name,
  label,
  placeholder,
  disabled,
  onPlaceSelected,
}: AddressInputProps) {
  const [{ value }, , { setValue }] = useField(name);

  const { placePredictions, getPlacePredictions, placesService } =
    usePlacesService({
      apiKey: PLACES_API_KEY,
      debounce: 250,
    });

  const options = useMemo(
    () => placePredictions.map(p => p.description),
    [placePredictions],
  );

  const handlePlaceSelect = (placeId: string) => {
    placesService?.getDetails(
      {
        placeId,
      },
      placeResult => {
        if (placeResult) {
          onPlaceSelected(parsePlaceResult(placeResult));
        }
      },
    );
  };

  return (
    <InputWrapper label={label} name={name}>
      <Autocomplete
        freeSolo
        disableClearable
        disabled={disabled}
        options={options}
        clearOnBlur={false}
        clearOnEscape={false}
        selectOnFocus={false}
        inputValue={value || ''}
        onInputChange={(e, newValue, reason) => {
          if (reason === 'input') {
            setValue(newValue);
            getPlacePredictions({ input: newValue || '' });
          }
        }}
        onChange={(e, newValue) => {
          if (newValue) {
            const selectedPrediction = placePredictions.find(
              p => p.description === newValue,
            );
            if (selectedPrediction) {
              handlePlaceSelect(selectedPrediction.place_id);
            }
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            id={name}
            placeholder={placeholder}
            sx={theme => ({
              boxSizing: 'border-box',
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: important('0.5px'),
                borderColor: important(theme.palette.lightOutlineGrey.main),
                borderRadius: important('10px'),
              },
              p: 0,
            })}
            slotProps={{
              input: {
                ...params.InputProps,
                sx: theme => ({
                  p: important('16.5px 14px'),
                  boxSizing: 'border-box',
                  height: important('44px'),
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: important('0.5px'),
                    borderColor: important(theme.palette.lightOutlineGrey.main),
                    borderRadius: important('10px'),
                  },
                }),
              },
              htmlInput: {
                ...params.inputProps,
                sx: {
                  p: important(0),
                },
              },
              inputLabel: params.InputLabelProps,
            }}
            autoComplete="street-address"
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Typography variant="body2">{option}</Typography>
          </li>
        )}
        getOptionKey={option => option}
        getOptionLabel={option => option}
      />
    </InputWrapper>
  );
}

export default AddressInput;
